import * as React from 'react';
import bind from 'bind-decorator';
import FormUtils from '../utils/FormUtils';
import { uniq } from 'lodash-es';
import '../styles/TableQuestion.scss';
import { DataPoint } from '../../../Interfaces/DataPoint';
import TableRow from './TableRow';
import { initDataPoint } from '../utils/utils';
import { getReferencedQuestions, getReferenceTableValue } from '../utils/TableQuestionUtils';
import { createRequestFilters } from '../../../utils/utils';
import { TableQuestionPropsInterface } from '../Containers/TableQuestionContainer';
import { questionTypes } from '../../../Interfaces/Forms/QuestionInterface';
const uuid = require('uuid/v4');
import { Resizable } from 're-resizable';
import SingleInstanceChartComponent from '../../Charts/SingleInstanceChartComponent';
import SingleInstanceChartsSelector from '../../Charts/SingleInstanceChartsSelector';
import Transmit from './transmit';

interface State {
  value: DataPoint[];
  subFormDataPoints: DataPoint[];
  height: number | undefined;
}

export default class TableQuestion extends React.Component <TableQuestionPropsInterface, State> {
  private formUtils: FormUtils;
  private headerEl: JSX.Element | null = null;
  private subFormUtils: FormUtils | undefined = undefined;

  private abortController: AbortController = new AbortController();

  private sumFields: string[] = [];
  private avgFields: string[] = [];

  private divEl = React.createRef<HTMLDivElement>();

  constructor(props: TableQuestionPropsInterface) {
    super(props);
    const { dataPoint, question, tableForm, subForm } = this.props;
    this.formUtils = new FormUtils(tableForm);
    if (subForm) {
      this.subFormUtils = new FormUtils(subForm);
    }
    this.state = question.type === 'ReferenceTableQuestion' ? this.getReferencedSubformValues() : {
      value: dataPoint[question.id] ? dataPoint[question.id] : [],
      subFormDataPoints: [],
      height: undefined
    };

    for (const qn of tableForm.question) {
      if (qn.operation === 'SUM') {
        this.sumFields.push(qn.id);
      } else if (qn.operation === 'AVG') {
        this.avgFields.push(qn.id);
      }
    }
  }

  @bind
  private getReferencedSubformValues(): State {
    const { formUtils, dataPoint, question, tableForm, subForm } = this.props;
    const oldValue = dataPoint[question.id] ? dataPoint[question.id] : [];
    if (subForm && subForm.isChild) {
      const subFormQuestionIds = formUtils.getSubFormQuestionIds();
      let subFormDataPoints: DataPoint[] = [];
      subFormQuestionIds.forEach((id) => {
        if (dataPoint[id]) {
          const data = dataPoint[id];
          if (data.length > 0 && data[0].questionnaire_id === question.value) {
            subFormDataPoints = subFormDataPoints.concat(data);
          }
        }
      });
      const value = getReferenceTableValue(
        question, this.subFormUtils, subFormDataPoints, oldValue, tableForm
      );
      const { updateAnswer } = this.props;
      if (updateAnswer) {
        const newAns = {};
        newAns[question.id] = value;
        updateAnswer(newAns);
      }
      return { value, subFormDataPoints, height: undefined };
    } else {
      this.loadReferencedForm();
      return { value: oldValue, subFormDataPoints: [], height: undefined };
    }
  }

  @bind
  private loadReferencedForm() {
    const {
      question, fetchPOI, subForm, filtersMenu, locationHierarchyQuery, tableForm, dataPoint, formUtils
    } = this.props;
    if (subForm) {
      let skipFilter = false;
      const fields = getReferencedQuestions(tableForm, this.subFormUtils);
      fields.push('id');
      this.createAbortController();
      const filters = createRequestFilters(filtersMenu);
      if (question.script && question.script !== '') {
        const allVariables: string[] = uniq(question.script.match(/_[0-9a-zA-Z_$]*\.[0-9a-zA-Z_.$]*|_[0-9a-zA-Z_$]*/g));
        const tempFilter = {
          field: '',
          value: '',
          condition: '',
          operator: 1
        };
        let script = question.script;
        script = script.substring(script.indexOf('(') + 1, script.lastIndexOf(')')).trim();

        for (const variable of allVariables) {
          script = script.replace(variable, '');
          if (variable.startsWith(`_${subForm.referenceName}`)) {
            const refName = variable.substring(variable.indexOf('.') + 1);
            const questionId = this.subFormUtils?.getQuestionId(`_${refName}`);
            if (questionId) {
              const qn = this.subFormUtils?.getQuestion(questionId);
              if (qn && qn.type !== questionTypes.SELECT_MULTIPLE_QUESTION
                && qn.type !== questionTypes.SELECT_ONE_QUESTION
                && qn.type !== questionTypes.LIKERT_SCALE_QUESTION) {
                tempFilter.field = questionId;
              } else {
                if (fields.indexOf(questionId) === -1) {
                  fields.push(questionId);
                }
                skipFilter = true;
              }
            }
          } else {
            const questionId = formUtils.getQuestionId(variable);
            tempFilter.value = `${dataPoint[questionId]}`;
          }
        }
        const values = script.match(/\'[a-zA-Z0-9]*\'|\"[a-zA-Z0-9]*\"/g);
        if (values) {
          script = script.replace(values[0], '').trim();
          tempFilter.value = values[0].replace(/\'|\"/g, '');
        }
        // const scriptParts = script.trim().split(' ');
        switch (script.trim()) {
          case '==':
            tempFilter.condition = 'EQUAL';
            break;
          case '>=':
            tempFilter.condition = 'GREATER_THAN_OR_EQUAL';
            break;
          case '<=':
            tempFilter.condition = 'LESS_THAN_OR_EQUAL';
            break;
          case '!=':
            tempFilter.condition = 'NOT_EQUAL';
            break;
          case '>':
            tempFilter.condition = 'GREATER_THAN';
            break;
          case '<':
            tempFilter.condition = 'LESS_THAN';
            break;
          default:
            tempFilter.condition = 'EQUAL';
        }
        if (!skipFilter) {
          filters.push(tempFilter);
        }
      }
      const request = fetchPOI(
        subForm.ref, this.abortController.signal, undefined, filters, locationHierarchyQuery, fields.join(',')
      );
      this.poisLoaded(request);
    }
  }

  @bind
  private poisLoaded(requestPromise: Promise<Response>) {
    const { dataPoint, question, tableForm, formUtils } = this.props;

    requestPromise.then(response => response.json()).then((json) => {
      if (json && json.length > 0) {
        const oldValue = dataPoint[question.id] ? dataPoint[question.id] : [];
        const value = getReferenceTableValue(
          question, this.subFormUtils, json, oldValue, tableForm, formUtils, dataPoint
        );
        const { updateAnswer } = this.props;
        if (updateAnswer) {
          const newAns = {};
          newAns[question.id] = value;
          updateAnswer(newAns);
        }
        this.setState({ value, subFormDataPoints: json });
      }
    }).catch((error) => {
      console.log(error);
    });
  }

  @bind
  private createAbortController() {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
  }

  /*
    For dynamic tables, removes a row.
  */
  @bind
  private removeRow(i: number) {
    const { value } = this.state;
    const newValue = Object.assign([], value);
    newValue.splice(i, 1);
    this.setState({ value: newValue }, this.updateMainPOI);
  }

  /*
    For dynamic tables, adds a new row.
  */
  @bind
  private addRow() {
    const { value } = this.state;
    const { tableForm } = this.props;
    const newValue = Object.assign([], value);
    const newRow = initDataPoint({}, tableForm);
    newRow.id = uuid();
    newValue.push(newRow);
    this.setState({ value: newValue }, this.updateMainPOI);
  }

  /*
    Updates the main POI value.
  */
  @bind
  private updateMainPOI() {
    const { value } = this.state;
    const { question, updateAnswer } = this.props;
    if (updateAnswer) {
      const newAns = {};
      newAns[question.id] = value;
      updateAnswer(newAns);
    }
  }

  @bind
  public updateAnswer(answer: DataPoint, index: number) {
    this.setState((prevState, props) => {
      const { value } = prevState;
      const newValue = [...value];
      newValue.splice(index, 1, answer);
      return { value: newValue };
    }, this.updateMainPOI);
  }

  /*
    Returns the header for the table.
  */
  @bind
  private getHeader(): JSX.Element {
    if (this.headerEl) {
      return this.headerEl;
    }
    const { tableForm, question } = this.props;
    const headers = [<th key={`${question.id}_${tableForm.id}`} className="counter">#</th>];

    const required = (
      <span className="text-danger">*</span>
    );
    let nameHeader = 'Name';
    for (const qn of tableForm.question) {
      if ('Name' === qn.id) {
        nameHeader = qn.text;
      }
      if (!qn.hideInSingleInstance && !qn.inVisible && !qn.deleted && qn.id !== 'subformID') {
        headers.push(
          <th key={`${question.id}_${tableForm.id}_${qn.id}`}>
            {qn.text ? qn.text : ''}
            {qn.optional ? null : required}
          </th>
        );
      }
    }
    if (question.table.rows && question.table.rows.listItem) {
      headers.splice(1, 0, <th key={`${question.id}_${tableForm.id}_name`}>{nameHeader}</th>);
    } else {
      headers.push(<th key={`${question.id}_${tableForm.id}_remove`} className="remove">{}</th>);
    }
    this.headerEl = (
      <tr>{headers}</tr>
    );
    return this.headerEl;
  }

  /*
    This function returns the static table rows.
    If the row has no data it would initialize a new data model which would be passed to the questions.
    It also adds the row name as a column after the count column.
  */
  @bind
  private getStaticRows(): JSX.Element[] {
    const { tableForm, question, forms, clientPersist } = this.props;
    const { value } = this.state;
    const rows: JSX.Element[] = [];
    let i = 0;
    for (const rowItem of question.table.rows.listItem) {
      let rowValue = value.find( v => {
        return v.Name === rowItem.id || v.Name === rowItem.value;
      });
      if (!rowValue && question.table.rows.listItem.length === value.length) {
        rowValue = question.table.rows.listItem[i];
      }
      if (!rowValue) {
        const tempDataPoint: DataPoint = { Name: rowItem.id ? rowItem.id : rowItem.value };
        rowValue = initDataPoint(tempDataPoint, tableForm);
      }
      const row = (
        <TableRow
          key={`${question.id}_${tableForm.id}_row_${i}`}
          dataPoint={rowValue}
          dynamic={false}
          formUtils={this.formUtils}
          rowItem={rowItem}
          updateAnswer={this.updateAnswer}
          index={i}
          question={question}
          forms={forms}
          clientPersist={clientPersist}
        />
      );
      rows.push(row);
      i++;
    }
    return rows;
  }

  /*
    returns the dynamic table rows for this table question.
  */
  @bind
  private getDynamicTableRows(): JSX.Element[] {
    const { tableForm, question, forms, clientPersist } = this.props;
    const { value } = this.state;
    const rows: JSX.Element[] = [];
    let i = 0;
    if (value.length === 0) {
      value[0] = initDataPoint({}, tableForm);
      value[0].id = uuid();
    }
    for (const val of value) {
      const row = (
        <TableRow
          key={`${question.id}_${tableForm.id}_row_${val.id}`}
          dataPoint={val}
          dynamic={true}
          formUtils={this.formUtils}
          removeRow={this.removeRow}
          rowItem={undefined}
          updateAnswer={this.updateAnswer}
          index={i}
          question={question}
          forms={forms}
          clientPersist={clientPersist}
        />
      );
      rows.push(row);
      i++;
    }
    return rows;
  }

  @bind
  private getReferenceTableRows(): JSX.Element[] {
    const { tableForm, question, forms, clientPersist, parentModel, parentDataPoint } = this.props;
    const { value, subFormDataPoints } = this.state;
    const rows: JSX.Element[] = [];
    let i = 0;

    for (const subFormDataPoint of subFormDataPoints) {
      const tableData = value.find( val => val.subformID === subFormDataPoint.id);
      if (tableData) {
        const row = (
          <TableRow
            key={`${question.id}_${tableForm.id}_row_${i}`}
            dataPoint={tableData}
            dynamic={true}
            formUtils={this.formUtils}
            rowItem={undefined}
            updateAnswer={this.updateAnswer}
            index={i}
            question={question}
            forms={forms}
            clientPersist={clientPersist}
            parentModel={parentModel}
            parentDataPoint={parentDataPoint}
          />
        );
        rows.push(row);
        i++;
      }
    }
    return rows;
  }

  @bind
  private getOperationsFooter(): JSX.Element | null {
    if (this.sumFields.length === 0 && this.avgFields.length === 0) {
      return null;
    } else {
      const { tableForm, question } = this.props;
      const { value } = this.state;
      const footers = [<td key={`${question.id}_${tableForm.id}_f`}/>];

      const sums = {};
      this.sumFields.forEach((id) => { sums[id] = 0; });
      this.avgFields.forEach((id) => { sums[id] = 0; });

      for (const val of value) {
        this.sumFields.forEach((id) => { sums[id] += val[id] || 0; });
        this.avgFields.forEach((id) => { sums[id] += val[id] || 0; });
      }

      for (const qn of tableForm.question) {
        if (!qn.hideInSingleInstance && !qn.inVisible && !qn.deleted && qn.id !== 'subformID') {
          let content;
          if (this.sumFields.indexOf(qn.id) !== -1) {
            content = 'Sum: ' + sums[qn.id];
          } else if (this.avgFields.indexOf(qn.id) !== -1) {
            content = 'Average: ' + sums[qn.id] / value.length;
          } else {
            content = '';
          }
          footers.push(
            <td key={`${question.id}_${tableForm.id}_${qn.id}_f`}>
              {content}
            </td>
          );
        }
      }
      return <tfoot><tr>{footers}</tr></tfoot>;
    }
  }

  @bind
  private getChartComponents() {
    const { question, tableForm, dataPoint } = this.props;
    if (question.type === questionTypes.TABLE_QUESTION && question.table.rows &&
      dataPoint.row_id && dataPoint.row_id > 0) {
      return [(
        <SingleInstanceChartComponent
          key={`single-instance-chart-component`}
          formId={tableForm.ref}
          rowId={dataPoint.row_id}
        />
      ), (
        <SingleInstanceChartsSelector
          key={`single-instance-chart-selector`}
          formId={tableForm.ref}
        />
      )];
    }
    return null;
  }

  public componentWillUnmount() {
    this.abortController.abort();
  }

  public componentDidMount() {
    const { question, dataPoint } = this.props;
    const hash = window.location.hash.split('/');
    if (question.actionPerRow && hash[4] === question.id && dataPoint.row_id && this.divEl.current) {
      const topPos = this.divEl.current.offsetTop - 100;
      window.scroll(0, topPos);
    }
  }

  public render(): JSX.Element | null {
    const { question, users, clientPersist, sendActionEmail, dataPoint, tableForm, getSharerEmail } = this.props;
    const { height } = this.state;
    const rows = question.table.rows && question.table.rows.listItem ? this.getStaticRows() :
      question.type === 'ReferenceTableQuestion' ? this.getReferenceTableRows() : this.getDynamicTableRows();
    //  :
    const addRowBtn = question.type === 'TableQuestion' && !question.table.rows ? (
      <button className="btn btn-primary btn-sm add-row" onClick={this.addRow}>Add row</button>
    ) : null;
    if (question.hideInSingleInstance) {
      return (
        <div className={'d-none'}>{rows}</div>
      );
    }
    const charts = this.getChartComponents();
    return (
      <div
        className="form-group col-md-12 col-lg-12 col-sm-12"
        ref={this.divEl}
      >
        {charts && charts[0]}
        <label className={'w-100'}>
          {question.text ? question.text : ''}
          {addRowBtn}
          {charts && charts[1]}
          {question.actionPerRow && dataPoint.row_id && (
            <Transmit
              users={users}
              clientPersist={clientPersist}
              link={`${dataPoint.questionnaire_id}/${dataPoint.id}/${dataPoint.row_id}/false/${question.id}`}
              questionId={question.id}
              tableFormId={tableForm.ref}
              id={dataPoint.id || ''}
              getSharerEmail={getSharerEmail}
              sendActionEmail={sendActionEmail}
            />
          )}
        </label>
        <Resizable
          size={{
            width: '100%',
            height: height || 'auto'
          }}
          className={`table-question-resizeable`}
          minHeight={'20vh'}
          minWidth={'100%'}
          enable={{
            top:false, right:false, bottom:true, left:false,
            topRight:false, bottomRight:false, bottomLeft:false, topLeft:false
          }}
          style={{
            borderBottom: '2px solid #ddd',
            overflow: 'auto'
          }}
          handleStyles={{
            bottom: {
              backgroundColor: '#ddd',
              height: '3px',
              width: '100%'
            }
          }}
          onResizeStop={(e, direction, ref, d) => {
            const h = this.state.height;
            const currentHeight = h ? h : Number(ref.clientHeight);
            this.setState({ height: currentHeight + d.height});
          }}
        >
          <div className={''}>
            <table className="table table-hover table-condensed">
              <thead>
                {this.getHeader()}
              </thead>
              <tbody>
                {rows}
              </tbody>
              {this.getOperationsFooter()}
            </table>
          </div>
        </Resizable>
      </div>
    );
  }
}
